// Sampling and the remote kill-switch.
//
// Sampling rates are configured per event-type. The kill-switch is a tiny
// JSON document fetched from `configUrl` at init (cached in sessionStorage
// for 5 minutes to keep it off the critical path). If it can't be fetched
// the SDK falls back to the in-code defaults — failing open means we'd
// rather over-collect than silently drop coverage.

import { onIdle, supports } from './safe.js';
import { applyConsentConfig } from './consent.js';

const KEY = 'amb.cfg';
const TTL_MS = 5 * 60 * 1000;

let _cfg = {
  enabled: true,
  sampling: {
    web_vital: 1.0,
    navigation_timing: 1.0,
    resource_timing: 0.1,
    long_task: 0.5,
    error: 1.0,
    page_view: 1.0,
    custom_action: 1.0
  },
  // Stable per-anonymousId sampling for traces — keeps a user's session
  // consistent (you either see all their traces or none). Vitals stay at 100%.
  sessionTraceRate: 0.1
};
let _killed = false;
let _anonId = null;

// Session-replay config — populated from the GET /v1/config `replay` block.
// `enabled` defaults false: replay is opt-in per app, there is no compiled-in
// "on" default. `onErrorRate` drives the error-triggered capture cohort.
let _replay = { enabled: false, onErrorRate: 0 };

// Heatmap config — populated from the GET /v1/config `heatmaps` block. Same
// opt-in story as replay: `enabled` defaults false and only ever turns on
// remotely; `sampleRate` drives the stable per-anonymousId capture cohort.
let _heatmaps = { enabled: false, sampleRate: 0.05 };

// One-shot signal: fires once the remote config has loaded (or the fetch has
// settled / there is no configUrl). The replay collector waits on it before
// deciding whether to record, since replay only ever turns on remotely.
let _configLoaded = false;
let _configListeners = [];

export function initConfig(cfg, anonymousId) {
  _anonId = anonymousId;
  if (cfg && cfg.sampling) Object.assign(_cfg.sampling, cfg.sampling);
  if (cfg && typeof cfg.sessionTraceRate === 'number') _cfg.sessionTraceRate = cfg.sessionTraceRate;

  if (cfg && cfg.configUrl) {
    loadRemote(cfg.configUrl);
  } else {
    // No remote config — replay stays disabled (the GET /v1/config `replay`
    // block is its only "on" source). Release any waiting listeners.
    fireConfigLoaded();
  }
}

export function isKilled() { return _killed || !_cfg.enabled; }

export function shouldSample(eventType) {
  const rate = _cfg.sampling[eventType];
  if (rate == null) return true;
  if (rate >= 1) return true;
  if (rate <= 0) return false;
  return Math.random() < rate;
}

// Stable per-anonymousId predicate for opt-in features (session replay, full
// resource traces). Hashing the anonymous_id gives the same answer for the
// same user across reloads — without this you'd get flickering coverage.
export function inSessionTraceCohort() {
  if (!_anonId) return false;
  if (_cfg.sessionTraceRate >= 1) return true;
  if (_cfg.sessionTraceRate <= 0) return false;
  // 32-bit FNV-1a; collision-prone for crypto but plenty for cohort assignment.
  let h = 0x811c9dc5;
  for (let i = 0; i < _anonId.length; i++) {
    h ^= _anonId.charCodeAt(i);
    h = (h * 0x01000193) >>> 0;
  }
  return (h / 0xffffffff) < _cfg.sessionTraceRate;
}

// Stable per-anonymousId predicate for error-triggered replay capture. A
// distinct cohort from inSessionTraceCohort — the anonymous_id is salted — so a
// user can land in the error cohort without being in the always-on one.
export function inErrorReplayCohort() {
  if (!_anonId) return false;
  const rate = _replay.onErrorRate;
  if (rate >= 1) return true;
  if (rate <= 0) return false;
  let h = 0x811c9dc5;
  const s = _anonId + ':err';
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h * 0x01000193) >>> 0;
  }
  return (h / 0xffffffff) < rate;
}

// Whether session replay is enabled for this app — the GET /v1/config `replay`
// block, which already AND's the app opt-in with the receiver's support.
// False until the remote config has loaded.
export function isReplayEnabled() { return _replay.enabled; }

// Whether heatmap capture is enabled for this app — the GET /v1/config
// `heatmaps` block, which already AND's the app opt-in with the receiver's
// support. False until the remote config has loaded.
export function isHeatmapsEnabled() { return _heatmaps.enabled; }

// Stable per-anonymousId predicate for heatmap capture. Salted (':hm') so the
// heatmap cohort is independent of the replay cohorts — a user can be in any
// combination of the three.
export function inHeatmapCohort() {
  if (!_anonId) return false;
  const rate = _heatmaps.sampleRate;
  if (rate >= 1) return true;
  if (rate <= 0) return false;
  let h = 0x811c9dc5;
  const s = _anonId + ':hm';
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h * 0x01000193) >>> 0;
  }
  return (h / 0xffffffff) < rate;
}

// Registers a one-shot callback fired once the remote config has settled.
// Fires synchronously if the config is already in.
export function onConfigLoaded(fn) {
  if (_configLoaded) { try { fn(); } catch (_) { /* silent */ } return; }
  _configListeners.push(fn);
}

function fireConfigLoaded() {
  if (_configLoaded) return;
  _configLoaded = true;
  const fns = _configListeners;
  _configListeners = [];
  for (const fn of fns) { try { fn(); } catch (_) { /* silent */ } }
}

function loadRemote(url) {
  // Read cached copy synchronously on init so the kill-switch can fire before
  // any collector runs. Revalidate in idle time.
  if (supports('sessionStorage')) {
    try {
      const raw = sessionStorage.getItem(KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && Date.now() - parsed.at < TTL_MS) apply(parsed.cfg);
      }
    } catch (_) { /* silent */ }
  }

  onIdle(async () => {
    try {
      const res = await fetch(url, { credentials: 'omit', mode: 'cors', cache: 'no-store' });
      if (!res.ok) return;
      const remote = await res.json();
      apply(remote);
      try { sessionStorage.setItem(KEY, JSON.stringify({ at: Date.now(), cfg: remote })); } catch (_) {}
    } catch (_) { /* silent */ }
    finally { fireConfigLoaded(); }
  });
}

function apply(remote) {
  if (!remote || typeof remote !== 'object') return;
  if (remote.enabled === false) { _killed = true; _cfg.enabled = false; return; }
  if (remote.sampling) Object.assign(_cfg.sampling, remote.sampling);
  if (typeof remote.sessionTraceRate === 'number') _cfg.sessionTraceRate = remote.sessionTraceRate;
  // Session-replay block (see docs/session-replay.md). Absent on older
  // receivers — replay then simply stays disabled.
  if (remote.replay && typeof remote.replay === 'object') {
    _replay.enabled = remote.replay.enabled === true;
    if (typeof remote.replay.onErrorSampleRate === 'number') _replay.onErrorRate = remote.replay.onErrorSampleRate;
    // replay.sampleRate is the always-on cohort rate; it mirrors
    // sessionTraceRate, so let it win when the block carries it.
    if (typeof remote.replay.sampleRate === 'number') _cfg.sessionTraceRate = remote.replay.sampleRate;
  }
  // Heatmap block. Absent on older receivers — heatmaps then simply stay
  // disabled, exactly like replay.
  if (remote.heatmaps && typeof remote.heatmaps === 'object') {
    _heatmaps.enabled = remote.heatmaps.enabled === true;
    if (typeof remote.heatmaps.sampleRate === 'number') _heatmaps.sampleRate = remote.heatmaps.sampleRate;
  }
  // Per-app consent options (§5). Absent on older receivers — applyConsentConfig
  // ignores undefined fields, so the SDK keeps its init-time consent behaviour.
  applyConsentConfig({
    implicitConsent: remote.implicitConsent,
    honorDoNotTrack: remote.honorDoNotTrack
  });
}
